import { UserAction } from "@brookbush/types";

import { useSignUp } from "@clerk/nextjs";
import type { AuthenticateWithRedirectParams } from "@clerk/types";
import { getCookie } from "cookies-next";
import { ChevronRight } from "lucide-react";
import { useRouter } from "next/compat/router";
import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import { memo } from "react";

import { useGlobalStore, useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";

const getCTA = (oauthProvider: string): string =>
	oauthProvider
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
		.join(" ");

const SignUpCard = ({ isTeamMembership }: { isTeamMembership?: boolean }) => {
	const { setUserAction } = useUserAction();
	const { signUp } = useSignUp();
	const { isGoogleSignInSupported } = useGlobalStore();

	const trackCTA = useTrackCTA();
	const router = useRouter();

	const path = router?.asPath.split("?")[0]!;

	const signUpWithSocial = async (
		strategy: AuthenticateWithRedirectParams["strategy"],
	) =>
		await signUp?.authenticateWithRedirect({
			redirectUrl: "/sign-up/sso-callback",
			redirectUrlComplete: path,
			strategy,

			unsafeMetadata: {
				ctaClick: getCTA(strategy),
				signUpPath: path,
				isTrialUser: !!getCookie("is-trial-user") || !!getCookie("referrer-id"),
				ipAddress: getCookie("ip-address"),
				userAgent: getCookie("user-agent"),
				auditedCourseId: getCookie("audited-course-id"),
				auditedVideoId: getCookie("audited-video-id"),
				auditedGlossaryTermId: getCookie("audited-glossary-term-id"),
				auditedSampleProgramId: getCookie("audited-sample-program-id"),
				referrerId: getCookie("referrer-id"),
				shouldReceiveGiftCard: getCookie("utm-gift-card"),

				posthogSessionId: posthog.get_session_id(),
			},
		});

	return (
		<div className="z-10 flex max-h-max w-full flex-col gap-6 rounded-t-xl bg-white p-6 pb-10 shadow-black/5 shadow-xl sm:m-auto sm:max-w-md sm:gap-7 sm:rounded-xl sm:p-8 dark:bg-slate-800 dark:shadow-black/30">
			<h2 className="text-center font-semibold text-2xl">
				{isTeamMembership
					? "Sign-in for team membership pricing and invoice creation."
					: "Become a member to get unlimited access today!"}
			</h2>

			<div className="mx-auto flex w-80 flex-col gap-6 font-medium text-lg">
				{isGoogleSignInSupported && (
					<button
						onClick={() => signUpWithSocial("oauth_google")}
						className="flex items-center gap-3 rounded-full border px-6 py-2 transition-all hover:bg-black/5"
					>
						<Image
							fetchPriority="high"
							src="/icons/google-icon.png"
							alt="Google Logo"
							width={24}
							height={24}
						/>
						Continue With Google
					</button>
				)}

				<button
					onClick={() => signUpWithSocial("oauth_facebook")}
					className="flex items-center gap-3 rounded-full border px-6 py-2 transition-all hover:bg-black/5"
				>
					<Image
						fetchPriority="high"
						src="/icons/facebook-icon.png"
						alt="Google Logo"
						width={24}
						height={24}
					/>
					Continue With Facebook
				</button>

				<button
					onClick={() => signUpWithSocial("oauth_apple")}
					className="flex items-center gap-3 rounded-full border px-6 py-2 transition-all hover:bg-black/5"
				>
					<Image
						fetchPriority="high"
						src="/icons/apple-icon.png"
						alt="Google Logo"
						width={24}
						height={24}
						className="dark:invert"
					/>
					Continue With Apple
				</button>

				<div className="flex items-center justify-center gap-4">
					<hr className="flex-1" />
					<span className="font-light">or</span>
					<hr className="flex-1" />
				</div>
				{isTeamMembership ? (
					<Link
						href="/become-a-member?type=team"
						onClick={() => trackCTA(UserAction.SIGN_UP, "Hero Card")}
						className="flex justify-center rounded-full bg-gradient-to-r from-sky-400 to-sky-600 px-4 py-2 text-white transition-all hover:brightness-95 dark:from-sky-500 dark:to-sky-700"
					>
						Sign Up with Email Address
					</Link>
				) : (
					<button
						type="button"
						onClick={() => {
							trackCTA(UserAction.SIGN_UP, "Hero Card");
							setUserAction(UserAction.SIGN_UP);
						}}
						className="flex justify-center rounded-full bg-gradient-to-r from-sky-400 to-sky-600 px-4 py-2 text-white transition-all hover:brightness-95 dark:from-sky-500 dark:to-sky-700"
					>
						Sign Up with Email Address
					</button>
				)}

				<div className="-mb-1 flex gap-1 font-normal sm:justify-center">
					Already Have an Account?{" "}
					<button
						onClick={() => setUserAction(UserAction.SIGN_IN)}
						className="group inline-flex max-w-max items-center gap-1 font-medium text-primary-600 hover:underline dark:text-primary-400"
					>
						Sign In
					</button>
				</div>

				{!isTeamMembership && (
					<Link
						href="/become-a-member?type=team"
						className="mx-auto -my-1 flex items-center gap-0.5 rounded-full border px-3 py-1 font-medium text-sm hover:bg-slate-100 sm:justify-center dark:hover:bg-slate-700"
					>
						Looking for team memberships?{" "}
						<ChevronRight size={18} className="-mr-1.5" />
					</Link>
				)}
			</div>
		</div>
	);
};

export default memo(SignUpCard);
